import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Stethoscope } from 'lucide-react';

const LoginPage: React.FC = () => {
  const location = useLocation();
  const isRegisterMode = useMemo(() => location.pathname === '/register', [location.pathname]);
  const [loginUsername, setLoginUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleRedirect = async () => {
    try {
      const response = await apiClient.get('/api/auth/next-task');
      const { redirect, patient_id } = response.data;

      if (redirect === 'consent') {
        navigate('/consent');
      } else if (redirect === 'survey') {
        navigate('/survey');
      } else if (redirect === 'group_c_selection') {
        navigate('/group-c-selection');
      } else if (redirect === 'chart') {
        navigate(`/patient-chart/${patient_id}`);
      } else if (redirect === 'done') {
        // Fallback to patient_0 chart showing 'All Tasks Completed'
        navigate('/patient-chart/patient_0');
      }
    } catch (err) {
      console.error('Failed to get next task:', err);
      navigate('/patient-chart/patient_0');
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await login(loginUsername, password);
      await handleRedirect();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Login failed');
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      await register({
        username: loginUsername,
        password,
      });
      await login(loginUsername, password);
      await handleRedirect();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Registration failed');
    }
  };

  const formTitle = isRegisterMode ? 'Create Account' : 'Login';
  const formDescription = isRegisterMode
    ? 'Create an account to explore 5 confirmation and 5 intervention sample cases.'
    : 'Please enter your credentials to access the study';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Stethoscope className="h-8 w-8 text-primary" />
            <h1 className="text-2xl font-bold">Medconceal</h1>
          </div>
        </div>

        {!isRegisterMode && (
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            <p className="font-medium mb-1">Shared demo accounts available</p>
            <p>You can log in with <span className="font-mono">test1</span> / <span className="font-mono">test1</span> through <span className="font-mono">test5</span> / <span className="font-mono">test5</span> to explore sample cases without registering.</p>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>{formTitle}</CardTitle>
            <CardDescription>{formDescription}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={isRegisterMode ? handleRegister : handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-username">Username</Label>
                <Input
                  id="login-username"
                  type="text"
                  placeholder="Enter your username"
                  value={loginUsername}
                  onChange={(e) => setLoginUsername(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="login-password">Password</Label>
                <Input
                  id="login-password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              {isRegisterMode && (
                <div className="space-y-2">
                  <Label htmlFor="register-confirm-password">Confirm Password</Label>
                  <Input
                    id="register-confirm-password"
                    type="password"
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>
              )}
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <Button type="submit" className="w-full">
                {isRegisterMode ? 'Register And Enter Samples' : 'Login'}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => navigate(isRegisterMode ? '/login' : '/register')}
              >
                {isRegisterMode ? 'Back To Login' : 'Create Account'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
